import { useEffect, useState } from 'react';

interface TourOverlayProps {
  targetSelector: string | null;
  onClickOutside: () => void;
}

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const PADDING = 8;
const BORDER_RADIUS = 12;

export function TourOverlay({ targetSelector, onClickOutside }: TourOverlayProps) {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!targetSelector) {
      setRect(null);
      return;
    }

    const updateRect = () => {
      const el = document.querySelector(`[data-tour="${targetSelector}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        // If element is hidden (display:none) or has zero dimensions, treat as not found
        if (r.width === 0 && r.height === 0) {
          setRect(null);
          return;
        }
        setRect({
          x: r.x - PADDING,
          y: r.y - PADDING,
          width: r.width + PADDING * 2,
          height: r.height + PADDING * 2,
        });
      } else {
        setRect(null);
      }
    };

    updateRect();

    // Update on scroll/resize
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [targetSelector]);

  // No target, or target exists but is hidden/zero-size: show a dimmed overlay (modal mode)
  if (!targetSelector || !rect) {
    return (
      <div
        className="fixed inset-0 z-[9998] bg-black/60 transition-opacity duration-300"
        onClick={onClickOutside}
      />
    );
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  return (
    <svg
      className="fixed inset-0 z-[9998] pointer-events-none"
      width={vw}
      height={vh}
      style={{ pointerEvents: 'auto' }}
      onClick={onClickOutside}
    >
      <defs>
        <mask id="tour-spotlight">
          <rect width={vw} height={vh} fill="white" />
          <rect
            x={rect.x}
            y={rect.y}
            width={rect.width}
            height={rect.height}
            rx={BORDER_RADIUS}
            ry={BORDER_RADIUS}
            fill="black"
          />
        </mask>
      </defs>
      {/* Dark overlay with cutout */}
      <rect
        width={vw}
        height={vh}
        fill="rgba(0,0,0,0.6)"
        mask="url(#tour-spotlight)"
      />
      {/* Highlight ring around target */}
      <rect
        x={rect.x}
        y={rect.y}
        width={rect.width}
        height={rect.height}
        rx={BORDER_RADIUS}
        ry={BORDER_RADIUS}
        fill="none"
        stroke="hsl(var(--ds-red))"
        strokeWidth="2"
        className="animate-pulse"
        style={{ pointerEvents: 'none' }}
      />
    </svg>
  );
}
